/**
 * Lo que se colecciona: insignias por niveles y trofeos de un solo disparo.
 *
 * Son dos cosas distintas a proposito, porque resuelven dos problemas distintos:
 *
 * - **Insignias**: un contador con cuatro escalones (bronce, plata, oro,
 *   leyenda). Nunca estan "hechas": siempre hay un numero a la vista y una
 *   barra a medias. Es lo que da un motivo para el partido siguiente incluso
 *   cuando ya llevas doscientos.
 * - **Trofeos**: un momento. No se acumulan, se consiguen — un hat-trick, una
 *   remontada, marcar en el ultimo minuto. Es lo que se cuenta luego.
 *
 * Lo que NO hay, y es deliberado: logros por tiempo jugado. "Juega 10 horas" es
 * una barra que se rellena sola estes haciendo lo que estes haciendo, y premia
 * dejar la pestana abierta en vez de jugar mejor.
 */
import { t } from "../i18n/index.js";
import type { CareerTotals, MatchOutcome, Profile } from "./profile.js";

export type TierIndex = 0 | 1 | 2 | 3 | 4;

export const TIER_NAMES: readonly string[] = [
  t("Sin empezar"),
  t("Bronce"),
  t("Plata"),
  t("Oro"),
  t("Leyenda"),
];

export interface Badge {
  id: string;
  label: string;
  hint: string;
  icon: string;
  /** Contador actual del jugador. */
  value(profile: Profile): number;
  /** Los cuatro escalones, de menor a mayor. */
  tiers: readonly [number, number, number, number];
}

/**
 * Los umbrales suben casi por diez, no por dos.
 *
 * Con escalones proximos los cuatro caen en la primera semana y despues no
 * queda nada. Asi bronce se toca enseguida (enganche), plata es un mes de
 * juego, oro es de los que se quedan y leyenda casi nadie: exactamente la
 * forma que tiene que tener para que ver a alguien con una signifique algo.
 */
export const BADGES: readonly Badge[] = [
  {
    id: "scorer",
    label: t("Goleador"),
    hint: t("Goles marcados"),
    icon: "⚽",
    value: (p) => p.totals.goals,
    tiers: [5, 25, 75, 200],
  },
  {
    id: "provider",
    label: t("Asistente"),
    hint: t("Asistencias repartidas"),
    icon: "🅰️",
    value: (p) => p.totals.assists,
    tiers: [3, 15, 50, 150],
  },
  {
    id: "wall",
    label: t("Recuperador"),
    hint: t("Balones robados"),
    icon: "🛡️",
    value: (p) => p.totals.recoveries,
    tiers: [25, 150, 500, 1500],
  },
  {
    id: "metronome",
    label: t("Metrónomo"),
    hint: t("Pases completados"),
    icon: "🎼",
    value: (p) => p.totals.passesOk,
    tiers: [100, 600, 2500, 8000],
  },
  {
    id: "star",
    label: t("Estrella"),
    hint: t("Veces MVP del partido"),
    icon: "⭐",
    value: (p) => p.totals.mvps,
    tiers: [1, 5, 20, 60],
  },
  {
    id: "winner",
    label: t("Ganador"),
    hint: t("Partidos ganados"),
    icon: "🏆",
    value: (p) => p.totals.wins,
    tiers: [1, 10, 50, 150],
  },
  {
    id: "veteran",
    label: t("Veterano"),
    hint: t("Partidos jugados"),
    icon: "🎖️",
    value: (p) => p.totals.matches,
    tiers: [5, 25, 100, 300],
  },
  {
    id: "keeper",
    label: t("Cerrojo"),
    hint: t("Partidos sin encajar"),
    icon: "🧤",
    value: (p) => p.totals.cleanSheets,
    tiers: [1, 5, 25, 75],
  },
  {
    id: "sniper",
    label: t("Francotirador"),
    hint: t("Tiros a puerta"),
    icon: "🎯",
    value: (p) => p.totals.shots,
    tiers: [20, 100, 400, 1200],
  },
  {
    id: "faithful",
    label: t("Fiel"),
    hint: t("Racha de días seguidos"),
    icon: "📅",
    value: (p) => Math.max(p.dayStreak, p.records.bestDayStreak),
    tiers: [3, 7, 21, 60],
  },
];

export interface BadgeProgress {
  badge: Badge;
  value: number;
  tier: TierIndex;
  /** Umbral del escalon siguiente, o null si ya esta al maximo. */
  next: number | null;
  /** 0-1 dentro del escalon actual. Al maximo, 1. */
  progress: number;
}

export function badgeProgress(badge: Badge, profile: Profile): BadgeProgress {
  const value = badge.value(profile);
  let tier = 0;
  for (const threshold of badge.tiers) {
    if (value >= threshold) tier += 1;
  }
  const next = tier >= badge.tiers.length ? null : (badge.tiers[tier] ?? null);
  const floor = tier === 0 ? 0 : (badge.tiers[tier - 1] ?? 0);
  const progress =
    next === null ? 1 : Math.min(1, Math.max(0, (value - floor) / Math.max(1, next - floor)));
  return { badge, value, tier: tier as TierIndex, next, progress };
}

export function allBadgeProgress(profile: Profile): BadgeProgress[] {
  return BADGES.map((b) => badgeProgress(b, profile));
}

/** Escalones conseguidos en total, para el contador de la pantalla. */
export function badgeTierTotal(profile: Profile): { done: number; max: number } {
  const done = BADGES.reduce((sum, b) => sum + badgeProgress(b, profile).tier, 0);
  return { done, max: BADGES.length * 4 };
}

// ------------------------------------------------------------------ trofeos

export type Rarity = "common" | "rare" | "epic" | "legendary";

export const RARITY_NAMES: Record<Rarity, string> = {
  common: t("Común"),
  rare: t("Poco común"),
  epic: t("Épico"),
  legendary: t("Legendario"),
};

export interface Trophy {
  id: string;
  label: string;
  hint: string;
  icon: string;
  rarity: Rarity;
  /** Se comprueba con el partido recien jugado y la carrera YA actualizada. */
  test(outcome: MatchOutcome, totals: CareerTotals, profile: Profile): boolean;
}

/**
 * Trofeos.
 *
 * Los identificadores de los primeros quince NO se tocan: son los que ya tiene
 * desbloqueados la gente que lleva jugando desde antes de esta pantalla, y
 * cambiar un id se los quitaria de golpe.
 */
export const TROPHIES: readonly Trophy[] = [
  {
    id: "first-goal",
    label: t("El primero"),
    hint: t("Marca tu primer gol"),
    icon: "⚽",
    rarity: "common",
    test: (_o, tot) => tot.goals >= 1,
  },
  {
    id: "first-assist",
    label: t("El último pase"),
    hint: t("Reparte tu primera asistencia"),
    icon: "🅰️",
    rarity: "common",
    test: (_o, tot) => tot.assists >= 1,
  },
  {
    id: "brace",
    label: t("Doblete"),
    hint: t("Dos goles en un mismo partido"),
    icon: "⚡",
    rarity: "common",
    test: (o) => (o.stat?.goals ?? 0) >= 2,
  },
  {
    id: "hat-trick",
    label: t("Hat-trick"),
    hint: t("Tres goles en un mismo partido"),
    icon: "🎩",
    rarity: "rare",
    test: (o) => (o.stat?.goals ?? 0) >= 3,
  },
  {
    id: "playmaker",
    label: t("Director de juego"),
    hint: t("Dos asistencias en un mismo partido"),
    icon: "🎯",
    rarity: "common",
    test: (o) => (o.stat?.assists ?? 0) >= 2,
  },
  {
    id: "first-mvp",
    label: t("El mejor"),
    hint: t("Sé MVP de un partido"),
    icon: "🏅",
    rarity: "common",
    test: (o) => o.isMvp,
  },
  {
    id: "mvp-five",
    label: t("Crack"),
    hint: t("Sé MVP en cinco partidos"),
    icon: "👑",
    rarity: "rare",
    test: (_o, tot) => tot.mvps >= 5,
  },
  {
    id: "clean-sheet",
    label: t("Portería a cero"),
    hint: t("Gana sin encajar"),
    icon: "🧤",
    rarity: "common",
    test: (o) => o.cleanSheet && o.result === "win",
  },
  {
    id: "comeback",
    label: t("Remontada"),
    hint: t("Gana un partido que ibas perdiendo"),
    icon: "🔥",
    rarity: "rare",
    test: (o) => o.comeback,
  },
  {
    id: "late-winner",
    label: t("En el descuento"),
    hint: t("Marca en el último minuto"),
    icon: "⏱️",
    rarity: "rare",
    test: (o) => o.lateGoal,
  },
  {
    id: "early-goal",
    label: t("Madrugador"),
    hint: t("Marca en los primeros 30 segundos"),
    icon: "🌅",
    rarity: "rare",
    test: (o) => o.earlyGoal,
  },
  {
    id: "ball-winner",
    label: t("Ladrón"),
    hint: t("Roba diez balones en un partido"),
    icon: "🥷",
    rarity: "rare",
    test: (o) => (o.stat?.recoveries ?? 0) >= 10,
  },
  {
    id: "streak-three",
    label: t("En racha"),
    hint: t("Gana tres partidos seguidos"),
    icon: "📈",
    rarity: "common",
    test: (_o, _tot, p) => p.winStreak >= 3,
  },
  {
    id: "regular",
    label: t("Habitual"),
    hint: t("Juega tres días seguidos"),
    icon: "📅",
    rarity: "common",
    test: (_o, _tot, p) => p.dayStreak >= 3,
  },
  {
    id: "veteran",
    label: t("Veterano"),
    hint: t("Juega veinticinco partidos"),
    icon: "🎖️",
    rarity: "rare",
    test: (_o, tot) => tot.matches >= 25,
  },

  // --- Nuevos. De aqui abajo, lo que cuesta de verdad.
  {
    id: "poker",
    label: t("Póker"),
    hint: t("Cuatro goles en un mismo partido"),
    icon: "🃏",
    rarity: "epic",
    test: (o) => (o.stat?.goals ?? 0) >= 4,
  },
  {
    id: "double-double",
    label: t("Doble-doble"),
    hint: t("Dos goles y dos asistencias en el mismo partido"),
    icon: "💫",
    rarity: "epic",
    test: (o) => (o.stat?.goals ?? 0) >= 2 && (o.stat?.assists ?? 0) >= 2,
  },
  {
    id: "assist-hat",
    label: t("Camarero"),
    hint: t("Tres asistencias en un mismo partido"),
    icon: "🍽️",
    rarity: "epic",
    test: (o) => (o.stat?.assists ?? 0) >= 3,
  },
  {
    id: "golden",
    label: t("Gol de oro"),
    hint: t("Marca el gol que acaba el partido en muerte súbita"),
    icon: "🥇",
    rarity: "epic",
    test: (o) => o.goldenGoalWinner,
  },
  {
    id: "flawless",
    label: t("Impecable"),
    hint: t("Acaba un partido sin perder un solo balón"),
    icon: "💎",
    rarity: "epic",
    // Con menos de quince toques no hay merito: no perder lo que no tocas es
    // gratis. El umbral es lo que separa jugar limpio de no jugar.
    test: (o) => (o.stat?.touches ?? 0) >= 15 && (o.stat?.losses ?? 0) === 0,
  },
  {
    id: "mvp-in-defeat",
    label: t("El único que se salvó"),
    hint: t("Sé MVP en un partido que perdiste"),
    icon: "🥀",
    rarity: "rare",
    test: (o) => o.isMvp && o.result === "loss",
  },
  {
    id: "comeback-two",
    label: t("Resurrección"),
    hint: t("Gana un partido en el que ibas dos goles por debajo"),
    icon: "🔱",
    rarity: "legendary",
    test: (o) => o.comeback && o.maxDeficit >= 2,
  },
  {
    id: "streak-seven",
    label: t("Intocable"),
    hint: t("Gana siete partidos seguidos"),
    icon: "🚀",
    rarity: "epic",
    test: (_o, _tot, p) => p.winStreak >= 7,
  },
  {
    id: "rating-70",
    label: t("Titular"),
    hint: t("Alcanza una media de 70"),
    icon: "📗",
    rarity: "rare",
    test: (_o, _tot, p) => p.overall >= 70,
  },
  {
    id: "rating-80",
    label: t("Franquicia"),
    hint: t("Alcanza una media de 80"),
    icon: "📘",
    rarity: "epic",
    test: (_o, _tot, p) => p.overall >= 80,
  },
  {
    id: "rating-90",
    label: t("Balón de oro"),
    hint: t("Alcanza una media de 90"),
    icon: "🏵️",
    rarity: "legendary",
    test: (_o, _tot, p) => p.overall >= 90,
  },
  {
    id: "level-ten",
    label: t("Profesional"),
    hint: t("Llega al nivel 10"),
    icon: "🔟",
    rarity: "rare",
    test: (_o, _tot, p) => p.level >= 10,
  },
  {
    id: "level-twentyfive",
    label: t("Carrera hecha"),
    hint: t("Llega al nivel 25"),
    icon: "🌟",
    rarity: "legendary",
    test: (_o, _tot, p) => p.level >= 25,
  },
  {
    id: "week-thirty",
    label: t("Semana redonda"),
    hint: t("Suma 30 puntos en una misma semana"),
    icon: "🗓️",
    rarity: "epic",
    test: (_o, _tot, p) => p.weekly.points >= 30,
  },
  {
    id: "thrashing",
    label: t("Manita"),
    hint: t("Gana un partido por cinco goles de diferencia"),
    icon: "🖐️",
    rarity: "epic",
    test: (o) => o.goalsFor - o.goalsAgainst >= 5,
  },
];

export function trophyById(id: string): Trophy | undefined {
  return TROPHIES.find((tr) => tr.id === id);
}
